import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import { NOTIFICATION_CENTER_PATH_NAME } from './notification-center.constants';
import {
  ApplicationNotificationActionsResponseSchema,
  ApplicationNotificationHealthIssuesResponseSchema,
  ApplicationConfigCompletenessResponseSchema,
  DismissNotificationResponseSchema,
  GetApplicationConfigCompletenessQuerySchema,
  GetApplicationNotificationActionsQuerySchema,
  GetApplicationNotificationHealthIssuesQuerySchema,
  GetGlobalNotificationInboxQuerySchema,
  GlobalNotificationInboxResponseSchema,
  MarkEntityViewedBodySchema,
  MarkGlobalInboxSeenResponseSchema,
  MarkEntityViewedResponseSchema,
  NotificationIdParamsSchema,
  NotificationTargetQuerySchema,
} from './notification-center.schema';

export const NotificationCenterContract = terraContract
  .prefix(`/${NOTIFICATION_CENTER_PATH_NAME}`)
  .router({
    getApplicationNotificationActions: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/actions',
        summary: 'Get paginated action notifications for a target',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: GetApplicationNotificationActionsQuerySchema }))
      .output(ApplicationNotificationActionsResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    getApplicationNotificationHealthIssues: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/health-issues',
        summary: 'Get paginated health issue notifications for a target',
        inputStructure: 'detailed',
      })
      .input(
        z.object({ query: GetApplicationNotificationHealthIssuesQuerySchema }),
      )
      .output(ApplicationNotificationHealthIssuesResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    getNotifications: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary:
          'Get tenant-wide open actions and health issues sorted by createdAt then id',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: GetGlobalNotificationInboxQuerySchema }))
      .output(GlobalNotificationInboxResponseSchema),
    markGlobalInboxSeen: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/seen',
        summary: 'Advance the current user last-seen watermark',
        inputStructure: 'detailed',
      })
      .input(z.object({}))
      .output(MarkGlobalInboxSeenResponseSchema),
    dismissNotification: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/actions/{notificationId}/dismiss',
        summary: 'Dismiss an action notification for the current user',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: NotificationIdParamsSchema,
          query: NotificationTargetQuerySchema,
        }),
      )
      .output(DismissNotificationResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    markEntityViewed: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/actions/entity-viewed',
        summary: 'Resolve view-based action notifications for an entity',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: NotificationTargetQuerySchema,
          body: MarkEntityViewedBodySchema,
        }),
      )
      .output(MarkEntityViewedResponseSchema),
    getApplicationConfigCompleteness: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/config-completeness',
        summary: 'Get target configuration completeness score and gaps',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: GetApplicationConfigCompletenessQuerySchema }))
      .output(ApplicationConfigCompletenessResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    dismissConfigCompletenessGap: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/config-completeness/{notificationId}/dismiss',
        summary:
          'Dismiss a configuration completeness gap for the current user',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: NotificationIdParamsSchema,
          query: NotificationTargetQuerySchema,
        }),
      )
      .output(DismissNotificationResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
  });
